/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  SageLogger,
  KvStore,
  ChunkStores,
  SageContextSource
} from "@tivocorp/sage-web-sdk/api";
import { SlsEndpointKind } from "@tivocorp/sage-web-sdk/api";
import {
  SageLoggerImpl,
  LoggingWindow,
  openSageDatabase,
  AppName,
  UuidNameEx,
  DeviceFamily,
  OperatingSystem,
  DevelopmentPhase,
  DiagnosticLevel
} from "@tivocorp/sage-web-sdk";
import { TsriServiceEndpointService } from "@tivocorp/data-domain-lib";

import { Log } from "./Log";
import { SageKvStore } from "./SageKvStore";
import { InMemoryKvStore } from "./InMemoryKvStore";
import { InMemoryChunkStore } from "./InMemoryChunkStore";
import { DiagnosticSeverity } from "../types";
import { LOG_CONTROL_STATUS_EVENT } from "./LogControlStatusConstants";
import { TIVO_SERVICES } from "../../config";

const TAG = "Sage";

/**
 * Which TSRI service backs each Sage HEC endpoint kind.
 *
 * The SDK has no SLS client and no refresh timer of its own: the host resolves
 * the host-level base (`https://<hostname>:<port>`) from TSRI and pushes it via
 * `setSlsEndpoint`. Same mapping as unity-android's `SageSlsEndpointSync`.
 *
 * A function, not a module-level constant: `config.ts` transitively imports
 * this module, so `TIVO_SERVICES` is still undefined while `Sage.ts` is being
 * evaluated in that import order. Reading it at call time sidesteps the cycle.
 */
function slsKindToTsriService(): ReadonlyArray<
  readonly [SlsEndpointKind, string]
> {
  return [
    [SlsEndpointKind.OPERATIONS, TIVO_SERVICES.SageSecure],
    [SlsEndpointKind.ANALYTICS, TIVO_SERVICES.SageAnalytics],
    [SlsEndpointKind.DIAGNOSTICS, TIVO_SERVICES.SageDiagnostics]
  ];
}

/**
 * Storage quota threshold (MB) below which we limit chunk count.
 * SmartTV devices (Tizen/WebOS) can have as low as 10-20MB IDB quota
 * shared with the app's own StorageAdapter.
 * See: tivocorp/sage-client-sdk PR #2 (nikhilxperi — navigator.storage.estimate() discussion)
 */
const STORAGE_LOW_THRESHOLD_MB = 20;

/**
 * Conservative chunk count for constrained/in-memory storage.
 * SDK defaults are 25 (ops/analytics) and 500 (diagnostics).
 * 10 is enough for ~30s flush cycles while limiting storage/RAM pressure.
 */
const CONSTRAINED_MAX_CHUNK_COUNT = 10;

export type SageDiagnosticLogLevel =
  | "TRACE"
  | "DEBUG"
  | "INFO"
  | "WARN"
  | "ERROR";

const SAGE_DIAGNOSTIC_LEVELS: Record<SageDiagnosticLogLevel, DiagnosticLevel> =
  {
    TRACE: DiagnosticLevel.TRACE,
    DEBUG: DiagnosticLevel.DEBUG,
    INFO: DiagnosticLevel.INFO,
    WARN: DiagnosticLevel.WARN,
    ERROR: DiagnosticLevel.ERROR
  };

const APPLICATION_DIAGNOSTIC_LEVELS: Record<number, DiagnosticLevel> = {
  [DiagnosticSeverity.Debug]: DiagnosticLevel.DEBUG,
  [DiagnosticSeverity.Info]: DiagnosticLevel.INFO,
  [DiagnosticSeverity.Warning]: DiagnosticLevel.WARN,
  [DiagnosticSeverity.Error]: DiagnosticLevel.ERROR
};

/**
 * Fire-and-forget facade for Sage telemetry.
 *
 * Callers use static methods synchronously — the facade internally queues
 * operations on a ready promise that resolves once the SDK is initialised.
 * If initialisation fails, a warning is logged once and all calls become no-ops.
 *
 * Note: If IDB is unavailable, the app's own StorageAdapter (also IDB-backed)
 * won't work either. This means saved tokens/provision data are lost — the app
 * goes through sign-in → provisioning → storeProvisionDeviceData() calls
 * Sage.setBodyId()/Sage.setPartnerId() anyway. So identity is always restored
 * via provisioning flow regardless of storage backend.
 */
export class Sage {
  private static ready: Promise<SageLogger | null> = Promise.resolve(null);
  private static initCalled = false;
  private static resolvedLogger: SageLogger | null = null;

  /**
   * Start Sage initialisation. Synchronous — kicks off an async chain internally.
   * Safe to call log methods immediately after.
   */
  public static init(contextSources: SageContextSource[] = []): void {
    if (Sage.initCalled) {
      Log.w(TAG, "init(): Already initialised");
      return;
    }
    Sage.initCalled = true;

    Sage.ready = openSageDatabase()
      .then(
        async (
          chunkStores: ChunkStores
        ): Promise<{
          kvStore: KvStore;
          chunkStores: ChunkStores;
          maxChunkCount?: number;
        }> => {
          const maxChunkCount = await Sage.estimateMaxChunkCount();
          return { kvStore: new SageKvStore(), chunkStores, maxChunkCount };
        }
      )
      .catch(
        (): {
          kvStore: KvStore;
          chunkStores: ChunkStores;
          maxChunkCount: number;
        } => {
          Log.w(TAG, "IndexedDB unavailable — using in-memory storage");
          return {
            kvStore: new InMemoryKvStore(),
            chunkStores: {
              operations: new InMemoryChunkStore(),
              analytics: new InMemoryChunkStore(),
              diagnostics: new InMemoryChunkStore()
            },
            maxChunkCount: CONSTRAINED_MAX_CHUNK_COUNT
          };
        }
      )
      .then(({ kvStore, chunkStores, maxChunkCount }) => {
        const logger = new SageLoggerImpl({
          appName: AppName.UUID_NAME_EX,
          uuidNameEx: UuidNameEx.NEON,
          deviceFamily: DeviceFamily.WEB,
          operatingSystem: OperatingSystem.WEB,
          developmentPhase:
            process.env.NODE_ENV === "production"
              ? DevelopmentPhase.PRODUCTION
              : DevelopmentPhase.DEVELOPMENT,
          kvStore,
          chunkStores,
          ...(maxChunkCount !== undefined && { maxChunkCount }),
          contextSources,
          diagnosticsBudgetExemptPrefixes: [LOG_CONTROL_STATUS_EVENT]
        });
        Sage.resolvedLogger = logger;
        Log.i(TAG, "SageLogger initialised");
        return logger;
      })
      .catch((error: unknown) => {
        Log.e(TAG, "SageLogger creation failed — telemetry disabled", error);
        return null;
      });
  }

  /**
   * Session id of the initialised logger, or undefined if the SDK has not
   * finished initialising (or initialisation failed). Used by the operational
   * logger to prefix generated transaction ids.
   */
  public static getSessionId(): string | undefined {
    if (Sage.resolvedLogger === undefined || Sage.resolvedLogger === null) {
      return undefined;
    }
    return Sage.resolvedLogger.getSessionId();
  }

  /**
   * Push the TSRI-resolved Sage HEC bases into the SDK, one per endpoint kind.
   *
   * The SDK persists the last pushed base per kind and restores it on the next
   * start, so this only needs to run when the TSRI catalog has been (re)read.
   * A kind whose TSRI service is absent is skipped and keeps the SDK's
   * compiled-in default (or the base restored from the SDK's own cache).
   * Fail-open: a failing kind never blocks the remaining ones — telemetry
   * routing must not take the app down.
   */
  public static populateTsriEndpoints(): void {
    for (const [kind, serviceName] of slsKindToTsriService()) {
      let baseUrl: string;
      try {
        baseUrl =
          TsriServiceEndpointService.getServiceEndpointByServiceName(
            serviceName
          );
      } catch {
        Log.w(
          TAG,
          `populateTsriEndpoints(): no TSRI entry for '${serviceName}' — ` +
            `${kind} endpoint keeps its default`
        );
        continue;
      }
      Sage.enqueue((sage) => {
        try {
          sage.setSlsEndpoint(kind, baseUrl);
          Log.i(TAG, `Pushed ${kind} HEC base to Sage SDK: ${baseUrl}`);
        } catch (error: unknown) {
          Log.e(TAG, `setSlsEndpoint(${kind}) failed`, error);
        }
      });
    }
  }

  private static enqueue(fn: (sage: SageLogger) => void): void {
    if (!Sage.initCalled) {
      Log.w(TAG, "Sage.init() not called — event dropped");
      return;
    }
    Sage.ready
      .then((sage) => {
        if (sage) fn(sage);
      })
      .catch((error: unknown) => {
        Log.e(TAG, "Sage enqueue failed", error);
      });
  }

  public static logOperations(
    name: string,
    details?: Record<string, string>
  ): void {
    Sage.enqueue((sage) => sage.logOperationsEvent(name, details));
  }

  public static logDiagnostics(
    name: string,
    message: string,
    severity: number,
    details?: Record<string, string>
  ): void {
    const sdkSeverity = APPLICATION_DIAGNOSTIC_LEVELS[severity];
    Sage.enqueue((sage) =>
      sage.logDiagnosticsEvent(name, message, sdkSeverity, details)
    );
  }

  public static logAnalytics(
    name: string,
    eventType?: string,
    details?: Record<string, string>
  ): void {
    Sage.enqueue((sage) => sage.logAnalyticsEvent(name, eventType, details));
  }

  public static setBodyId(id: string): void {
    Sage.enqueue((sage) => sage.setBodyId(id));
  }

  public static setPartnerId(id: number): void {
    Sage.enqueue((sage) => sage.setPartnerId(id));
  }

  public static setAnalyticsEnabled(enabled: boolean): void {
    Sage.enqueue((sage) => sage.setAnalyticsEnabled(enabled));
  }

  public static setLogLevel(level: SageDiagnosticLogLevel): void {
    Sage.enqueue((sage) => sage.setLogLevel(SAGE_DIAGNOSTIC_LEVELS[level]));
  }

  public static setBlacklist(tags: string[], version?: string): void {
    Sage.enqueue((sage) => sage.setBlacklist(tags, version));
  }

  public static setBackhaulEnabled(
    enabled: boolean,
    untilEpochMs?: number
  ): void {
    Sage.enqueue((sage) => sage.setBackhaulEnabled(enabled, untilEpochMs));
  }

  public static setBackhaulWindow(window: LoggingWindow | null): void {
    Sage.enqueue((sage) => sage.setBackhaulWindow(window));
  }

  public static setLoggingBudgets(
    diagPeriodSec: number,
    diagBudgetBytes: number,
    opsPeriodSec: number,
    opsBudgetBytes: number
  ): void {
    Sage.enqueue((sage) =>
      sage.setLoggingBudgets(
        diagPeriodSec,
        diagBudgetBytes,
        opsPeriodSec,
        opsBudgetBytes
      )
    );
  }

  public static flush(): void {
    Sage.enqueue((sage) => sage.flush());
  }

  /**
   * Estimate max chunk count based on available IDB quota.
   * Applies to all three log types (ops, analytics, diagnostics).
   *
   * - >= 20MB available: undefined (SDK defaults: 25 ops/analytics, 500 diagnostics)
   * - < 20MB available: 10 (constrained SmartTV devices)
   * - estimate() unavailable or throws: 10 (conservative fallback)
   * - in-memory path: 10 (no persistence, limit RAM usage)
   */
  private static async estimateMaxChunkCount(): Promise<number | undefined> {
    try {
      if (navigator.storage && navigator.storage.estimate) {
        const { quota, usage } = await navigator.storage.estimate();
        const availableMb = ((quota || 0) - (usage || 0)) / (1024 * 1024);
        if (availableMb < STORAGE_LOW_THRESHOLD_MB) {
          Log.w(
            TAG,
            "Low storage available (" +
              availableMb.toFixed(1) +
              "MB) — limiting chunk count"
          );
          return CONSTRAINED_MAX_CHUNK_COUNT;
        }
        return undefined;
      }
    } catch {
      // navigator.storage.estimate() not supported
    }
    return CONSTRAINED_MAX_CHUNK_COUNT;
  }
}
